export type EnhanceStatus = "now" | "partial" | "next" | "later" | "skipped";
export type EnhancePhase = 1 | 2 | 3;

export const PRODUCT_LOOP = [
  "User",
  "Events",
  "Category segments",
  "Content",
  "Newsletter",
  "Campaign",
  "Delivery",
  "Events",
] as const;

export const WORKFLOW_TARGET = [
  { n: "1", to: "/audience", title: "Audience", body: "Combine country, category, activity, and consent — not only a static list." },
  { n: "2", to: "/content", title: "Content", body: "Blocks, RTL preview, and Altibbi articles — not a blank email." },
  { n: "3", to: "/broadcasts", title: "Broadcast", body: "Pick a category segment, suppress ineligible people, then save a draft or schedule." },
  { n: "4", to: "/analytics", title: "Analytics", body: "What content and countries worked — then write the next issue." },
] as const;

export const ENHANCEMENTS: {
  id: string;
  title: string;
  priority: 5 | 4;
  phase: EnhancePhase;
  status: EnhanceStatus;
  href?: string;
  summary: string;
  points: string[];
}[] = [
  {
    id: "audience",
    title: "Audience / segmentation",
    priority: 5,
    phase: 1,
    status: "now",
    href: "/audience",
    summary: "Combine conditions so Marketing can target Vitamin D readers in Jordan who opted in — not just “the whole list.”",
    points: [
      "Category (Diabetes, Vitamin D, Nutrition) — same as CMS categories",
      "Behavior: last active, member type",
      "Lifecycle: opted in, consent, registered / last activity",
      "Profile: country, language, gender where appropriate",
      "AND / OR groups, e.g. Nutrition OR Weight Loss AND Jordan AND subscribed",
    ],
  },
  {
    id: "personalization",
    title: "Interest-based personalization",
    priority: 5,
    phase: 1,
    status: "skipped",
    href: "/audience",
    summary: "Skipped in Phase 1. Marketing already targets by category segment (Nutrition — Jordan). Per-person interest matching stays later.",
    points: [
      "Do not auto-pick a newsletter per person in this phase",
      "Use a category segment on the campaign instead",
      "Phase 3: automatic newsletter selection from a click-learned profile",
    ],
  },
  {
    id: "editor",
    title: "Newsletter editor",
    priority: 5,
    phase: 1,
    status: "now",
    href: "/content",
    summary: "Reusable blocks with drag, edit, and preview — Arabic RTL, desktop/mobile, and send metadata.",
    points: [
      "Blocks: hero, article, multiple articles, doctor answer, health tip, CTA, calculator, product, sponsored, footer",
      "Subject, preheader, sender, reply-to, UTM",
      "Personalization: مرحباً {{first_name}}",
      "Arabic RTL + desktop/mobile preview",
    ],
  },
  {
    id: "preview",
    title: "Preview / testing",
    priority: 4,
    phase: 1,
    status: "now",
    href: "/content",
    summary: "Test before send: devices, RTL, dark mode, and a real test inbox — plus safety checks.",
    points: [
      "Desktop, mobile, RTL, dark mode",
      "Send test email to selected addresses",
      "Broken links, missing images, empty {{variables}}",
      "Spam risk, unsubscribe present, UTM valid",
    ],
  },
  {
    id: "schedule",
    title: "Campaign scheduling",
    priority: 5,
    phase: 1,
    status: "now",
    href: "/broadcasts",
    summary: "Pick an approved newsletter and a category segment, then save a draft or schedule.",
    points: ["Segment + issue + time", "Status: draft → scheduled → sent", "Schedule is blocked until preflight is clean"],
  },
  {
    id: "suppression",
    title: "Suppression / compliance",
    priority: 5,
    phase: 1,
    status: "now",
    href: "/broadcasts",
    summary: "First-class eligibility: selected vs eligible vs suppressed, before any send.",
    points: [
      "Exclude unsubscribed, hard bounce, complaints, suppressed, invalid, no consent",
      "Show 500,000 selected → 472,381 eligible → 27,619 suppressed",
    ],
  },
  {
    id: "analytics",
    title: "Campaign analytics",
    priority: 5,
    phase: 1,
    status: "now",
    href: "/analytics",
    summary: "Sent, delivered, open, click, unsub, bounce, complaints — plus content and country performance.",
    points: [
      "Delivery funnel metrics",
      "Content: which article got the clicks",
      "Audience: Jordan CTR vs Saudi Arabia CTR",
      "By category segment, not per-person interest",
    ],
  },
  {
    id: "utm",
    title: "UTM tracking",
    priority: 4,
    phase: 1,
    status: "now",
    href: "/content",
    summary: "Canonical UTM on every campaign link so Analytics and the site share one story.",
    points: ["utm_source, utm_medium, utm_campaign, utm_content", "Validate before send"],
  },
  {
    id: "ai-writer",
    title: "AI newsletter assistant",
    priority: 5,
    phase: 2,
    status: "next",
    href: "/content",
    summary: "“Generate newsletter with AI” from a brief — subject, preheader, intro, 3–5 Altibbi articles, CTA, audience, send time.",
    points: [
      "Recommend existing Altibbi medical content; do not invent clinical claims",
      "Fits the Arabic content library and Sina as a later companion",
    ],
  },
  {
    id: "ai-recs",
    title: "AI content recommendations",
    priority: 5,
    phase: 2,
    status: "partial",
    href: "/content",
    summary: "Suggest published Altibbi articles, medical terms, and URLs from the CMS catalog that match the newsletter being written.",
    points: [
      "Typeahead in the designer searches content.content_index (articles, terms, news)",
      "Related panel uses the issue title, subject, and blocks",
      "Audience-aware ranking from clicks is still later — not interest personalization",
    ],
  },
  {
    id: "ab",
    title: "A/B testing",
    priority: 5,
    phase: 2,
    status: "later",
    summary: "Test subject, hero, CTA, article order, and send time — then pick the winner.",
    points: ["Subject A/B in Arabic", "Content variants", "Send-time test", "Auto-choose winner"],
  },
  {
    id: "calendar",
    title: "Campaign calendar",
    priority: 4,
    phase: 2,
    status: "later",
    href: "/broadcasts",
    summary: "Editorial + Marketing share one month view: date, campaign, audience, status.",
    points: ["Scheduled / draft / sent on a calendar"],
  },
  {
    id: "profile",
    title: "Subscriber profile / history",
    priority: 4,
    phase: 2,
    status: "partial",
    href: "/subscribers",
    summary: "One person: interests, consents, sends, opens, clicks, unsubs, content viewed, searches.",
    points: ["Richer than an email-sending tool", "Inspector today; full journey next"],
  },
  {
    id: "approval",
    title: "Approval workflow",
    priority: 4,
    phase: 2,
    status: "now",
    href: "/content",
    summary: "Medical content needs Draft → Review → Approved → Scheduled → Sent with people and timestamps.",
    points: ["Author, reviewer, approver", "Revision history", "Already started in the designer"],
  },
  {
    id: "per-user",
    title: "Personalized newsletter per user",
    priority: 5,
    phase: 3,
    status: "later",
    summary: "Automatically select the most relevant newsletter for each person from their interest profile.",
    points: ["Not a single blast to 500k"],
  },
  {
    id: "smart-send",
    title: "Smart send time",
    priority: 4,
    phase: 3,
    status: "later",
    summary: "Send when each person is most likely to engage, from past opens — not one 10:00 slot.",
    points: ["Later-stage; not MVP"],
  },
  {
    id: "interest-loop",
    title: "Interest learning from clicks",
    priority: 5,
    phase: 3,
    status: "later",
    summary: "Repeated Vitamin D / Magnesium / Calcium clicks raise confidence: interest = Vitamins & Minerals.",
    points: [
      "Behavior → interest profile → segment → recommendation → newsletter → behavior",
      "Lifecycle layer for Altibbi + Sina, not a Mailchimp clone",
    ],
  },
];

export const PHASES: { id: EnhancePhase; title: string; blurb: string }[] = [
  { id: 1, title: "Phase 1 — Must have", blurb: "Category segments, editor, preview + test, schedule, suppression, analytics, UTM. Interest personalization is skipped." },
  { id: 2, title: "Phase 2 — High value", blurb: "AI generator, content recommendations, A/B, calendar, subscriber history, stronger approval." },
  { id: 3, title: "Phase 3 — Intelligence", blurb: "Per-user newsletter, send-time optimization, automatic interest inference, predictive engagement." },
];
